/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { createRef } from "react";
import classNames from "classnames";
import MaskedInput from "react-text-mask";
import PropTypes from "prop-types";
import { StateSelection } from "@components/common";
import utils from "@utils";
import { DISPUTE_FEE_TYPE } from "@constants";
import { useGetDisputeById } from "@queries/NonRKPayInvoiceQueries";
import { V3_INVOICE_TYPE_ID } from "@constants/index";
import { ReactSelect, Loader, Image, Switch } from "../../../../../components";
import "../NonRKPayInvoiceDetail.scss";
import { MASK_VALUE } from "../../../../../js/constants";
import TrashIcon from "../../../../../images/trash-icon.svg";
import ConnectedEstimateLink from "./ConnectedEstimateLink";
import { useDisputeContext } from "../../context/DisputeContext";
import AvailableFundsDisplay from "./AvailableFundsDisplay";
import SectionSeparator from "./SectionSeparator";
import InvoiceDisclaimer from "./InvoiceDisclaimer";
import RefundsDisplay from "./RefundsDisplay";
import PaymentsAppliedDisplay from "./PaymentsAppliedDisplay";
import NonRKPayInvoiceItemView from "./NonRKPayInvoiceItem/NonRKPayInvoiceItemView";
import NonRKPayInvoiceItemForm from "./NonRKPayInvoiceItem/NonRKPayInvoiceItemForm";
import OfficerNonRKPayInvoiceItems from "./OfficerNonRKPayInvoiceItems";
import ConnectedInvoiceLink from "./ConnectedInvoiceLink";

const NonRKPayInvoiceDetailForm = ({
	user,
	nonRKPayInvoiceDetailHook,
	query,
	clientAddress,
	officerMiscFeesV3,
	clientV4InvoicingDisputes
}) => {
	const { lang } = global;
	let _stateSelection = createRef();
	const dateMask = [/[0-1]/, /[0-9]/, "/", /[0-3]/, /[0-9]/, "/", /2/, /0/, /[2-9]/, /[0-9]/];
	const {
		registerCardRef,
		openDisputeReason,
	} = useDisputeContext()

	const {
		invoiceState,
		loadingInvoice,
		isInvoicingV3,
		handleFromChange,
		selectedClient,
		clientList,
		handleClientChange,
		paymentHistory,
		handleBillToChange,
		handleBillToStateChange,
		toggleOfficerView,
		confirmLineItemsRefresh,
		loadingInvoiceTotals,
		excludeOfficerRate,
		loadingLineItems,
		customFee,
		handleCustomFeeChange,
		createCustomFee,
		deleteCustomFee,
		showPaymentHistory,
		refundsInTransit,
		handleInvoiceChange,
		togglePaymentHistory,
		handleClickAddJobs,
		availableFunds,
		nonrkpayDetailPrepayEnhancementsEnabled,
		initialized,
		associatedInvoices,
		toggleConnectedInvoicesModal
	} = nonRKPayInvoiceDetailHook;

	const {
		finalized,
		voided,
		amended,
		disputed,
		issueDate,
		dueDate,
		isEstimate,
		sentDateTimeUTC,
		invoiceId,
		invoiceNumber,
		invoiceDisclaimer,
		profilePicUrl,
		from,
		billTo,
		invoiceVersion,
		lineItems,
		totals,
		showFeesBreakdown,
		officerViewList,
		notesToClient,
		statusId,
		estimateId,
		estimateNumber,
		activeDisputeId,
		archived,
		estimateFinalized,
		typeId,
	} = invoiceState;

	const { data: dispute } = useGetDisputeById(activeDisputeId, {
		enabled: !!activeDisputeId && clientV4InvoicingDisputes,
	});

	const onChangeBillToState = event => {
		const {
			target: { value },
		} = event;
		const {
			state: { USStates },
		} = _stateSelection;
		let selectedState = "";
		if (value)
			selectedState = USStates.find(
				({ value: stateId }) => stateId.toString() === value,
			).label;
		const [stateAbbreviation = "", stateName = ""] = selectedState.split(" - ");
		handleBillToStateChange(value, stateName, stateAbbreviation);
	};

	const getDisputeReasonsByJobId = jobId => {
		if (clientV4InvoicingDisputes && dispute) {
			const disputeTypes = [
				'HoursIncorrectDisputes',
				'RateIncorrectDisputes',
				'JobNotWorkedDisputes',
				'JobAlreadyPaidDisputes'
			];

			const disputeReasons = [];
			for (let i = 0; i < disputeTypes.length; i++) {
				const disputeType = disputeTypes[i];
				if (dispute[disputeType] && dispute[disputeType].length > 0) {
					const jobDisputeReason = dispute[disputeType].find(
						disputeReason => disputeReason.JobId === jobId
					);
					if (jobDisputeReason) {
						disputeReasons.push({
							...jobDisputeReason,
							Type: disputeType,
						});
					}
				}
			}
			return disputeReasons;
		}
		return null;
	}

	const showRefreshButton =
		invoiceId &&
		!voided &&
		(isEstimate ||
			(!isEstimate && invoiceVersion === 2 && !sentDateTimeUTC) ||
			(!isEstimate && invoiceVersion === 3 && !finalized));
	const buttonTitle =
		query.estimate === "true"
			? lang.t("Add jobs to your estimate")
			: lang.t("Add jobs to your invoice");

	const invoiceStatus = utils.mapNonRKPayInvoiceStatusIdToLabelType(statusId, voided, archived, estimateFinalized);
	const billingDetailsDispute = clientV4InvoicingDisputes ? dispute?.BillingDetailsIncorrectDisputes?.find(dr => dr.Id === openDisputeReason) : undefined;
	const feesAndTaxesDispute = clientV4InvoicingDisputes ? dispute?.FeesAndTaxesIncorrectDisputes?.find(dr => dr.Id === openDisputeReason) : undefined;

	const hideInvoiceLevelFees = isEstimate && typeId === V3_INVOICE_TYPE_ID.OFFICER;
	const hideAddJobsButton = !isEstimate && typeId === V3_INVOICE_TYPE_ID.OFFICER;

	return (
		<div id="nonrkpay-invoice-detail-form">
			<Loader active={loadingInvoice || !initialized} />
			<div className="row">
				<div className="col-xs-6">
					<span className={`hidden-print label label-${invoiceStatus.type}`}>
						{invoiceStatus.label}
					</span>
				</div>
			</div>
			{disputed && (
				<div className="row">
					<div className="col-xs-6">
						<span className="label label-danger">{lang.t("Disputed")}</span>
					</div>
				</div>
			)}
			{amended && (
				<div className="row">
					<div className="col-xs-6">
						<span className="label label-success">{lang.t("Amended")}</span>
					</div>
				</div>
			)}

			{/* Invoice # & Dates */}
			<div className="row form-section v-align">
				<div className="col-xs-12 col-sm-6 col-md-3 col-lg-2 form-group">
					<label htmlFor="invoiceNumber">{`${lang.t(isEstimate ? "Estimate" : "Invoice")} #`}</label>
					<input
						id="invoiceNumber"
						name="invoiceNumber"
						className="form-control"
						placeholder={`(${lang.t("Auto-generate")})`}
						value={invoiceNumber}
						onChange={handleInvoiceChange("invoiceNumber")}
					/>
					<div className="visible-print-block">{invoiceNumber}</div>
				</div>
				<div className="col-xs-12 col-sm-3 col-md-2 col-md-push-3 col-md-offset-1 col-lg-offset-3 form-group">
					<label htmlFor="issueDate">{lang.t("Date Created")}</label>
					<Choose>
						<When condition={utils.isDesktopSafari || utils.isIE}>
							<MaskedInput
								name="issueDate"
								id="issueDate"
								type="text"
								mask={dateMask}
								className="form-control"
								placeholder="mm/dd/yyyy"
								value={issueDate}
								onChange={handleInvoiceChange("issueDate")}
							/>
						</When>
						<Otherwise>
							<input
								name="issueDate"
								id="issueDate"
								className="form-control"
								type="date"
								value={issueDate}
								onChange={handleInvoiceChange("issueDate")}
							/>
						</Otherwise>
					</Choose>
					<div className="visible-print-block">
						{utils.formatDate(
							new Date(`${issueDate}T00:00:00`),
							"en-US",
							undefined,
							"2-digit",
						)}
					</div>
				</div>
				<div
					className={classNames("col-xs-12 col-sm-3 col-md-push-3 col-md-2 form-group", {
						hidden: isEstimate,
					})}
				>
					<label htmlFor="dueDate">{lang.t("Due Date")}</label>
					<Choose>
						<When condition={utils.isDesktopSafari || utils.isIE}>
							<MaskedInput
								name="dueDate"
								id="dueDate"
								type="text"
								mask={dateMask}
								className="form-control"
								placeholder="mm/dd/yyyy"
								value={dueDate}
								onChange={handleInvoiceChange("dueDate")}
							/>
						</When>
						<Otherwise>
							<input
								name="dueDate"
								id="dueDate"
								className="form-control"
								type="date"
								value={dueDate}
								min={issueDate}
								onChange={handleInvoiceChange("dueDate")}
								data-test-id="nonrkpayinvoicedetail__due-date"
							/>
						</Otherwise>
					</Choose>
					<div className="visible-print-block">
						{utils.formatDate(
							new Date(`${dueDate}T00:00:00`),
							"en-US",
							undefined,
							"2-digit",
						)}
					</div>
				</div>
			</div>
			{/* Invoice Associated Estimate */}
			<If condition={nonrkpayDetailPrepayEnhancementsEnabled}>
				<ConnectedEstimateLink
					estimateId={estimateId}
					estimateNumber={estimateNumber}
					rowClassName="row form-section v-align"
					labelClassName="col-xs-6 col-md-5 col-lg-4"
				/>
			</If>
			<ConnectedInvoiceLink
				associatedInvoices={associatedInvoices}
				isEstimate={isEstimate}
				toggleConnectedInvoicesModal={toggleConnectedInvoicesModal}
			/>
			{/* Invoice Notes to Client and logo */}
			<div className="row form-section v-align">
				<div className="col-xs-12 col-sm-6 col-md-5 col-lg-4 form-group">
					<label htmlFor="notesToClient">{lang.t("Notes to Client")}</label>
					<textarea
						name="notesToClient"
						id="notesToClient"
						rows="3"
						className="form-control"
						value={notesToClient}
						onChange={handleInvoiceChange("notesToClient")}
					/>
					<div className="visible-print-block text-justify">
						{notesToClient.split("\n").map(text => (
							<span key={text.replace(/\s/g, "")}>
								{text}
								<br />
							</span>
						))}
					</div>
				</div>
				<div className="col-xs-12 col-sm-6 col-md-5 col-md-offset-2 col-lg-offset-3">
					<Image
						className="img-responsive"
						src={profilePicUrl}
						fallbackSrc="avatar"
					/>
				</div>
			</div>
			<div
				ref={ref => {
					if (ref && billingDetailsDispute) {
						registerCardRef(billingDetailsDispute?.Id, ref)
					}
				}}
				className={classNames("row form-section v-align-xs", {
					'border-1 border-solid border-danger': Boolean(billingDetailsDispute)
				})}>
				{/* Invoice sender details */}
				<div className="col-xs-12 col-sm-6 col-md-5 col-lg-4">
					<h4 className="bold-text">{lang.t("From")}</h4>
					<div className="form-group">
						<label htmlFor="fromName">{lang.t("Name")}</label>
						<input
							id="fromName"
							name="fromName"
							className="form-control"
							value={from.name}
							onChange={handleFromChange("name")}
							data-test-id="nonrkpayinvoicedetail__from-name"
						/>
						<div className="visible-print-block">{from.name}</div>
					</div>
					<div className="form-group">
						<label htmlFor="fromAddress">{lang.t("Department Address")}</label>
						<textarea
							id="fromAddress"
							name="fromAddress"
							rows="2"
							className="form-control"
							value={from.address}
							onChange={handleFromChange("address")}
							data-test-id="nonrkpayinvoicedetail__from-address"
						/>
						<div className="visible-print-block">{from.address}</div>
					</div>
					<div className="form-group">
						<label htmlFor="fromPhone">{lang.t("Phone Number")}</label>
						<MaskedInput
							type="tel"
							id="fromPhone"
							name="fromPhone"
							mask={MASK_VALUE.phoneMask}
							className="form-control"
							value={from.phone}
							placeholder="(xxx) xxx-xxxx"
							onChange={handleFromChange("phone")}
							data-test-id="nonrkpayinvoicedetail__from-phone"
						/>
						<div className="visible-print-block">{from.phone}</div>
					</div>
					<div className="form-group">
						<label htmlFor="fromEmail">{lang.t("Email")}</label>
						<input
							type="email"
							id="fromEmail"
							name="fromEmail"
							className="form-control"
							value={from.email}
							onChange={handleFromChange("email")}
							data-test-id="nonrkpayinvoicedetail__from-email"
						/>
						<div className="visible-print-block">{from.email}</div>
					</div>
					<div className="form-group">
						<label htmlFor="fromContact">{lang.t("Contact")}</label>
						<input
							type="email"
							id="fromContact"
							name="fromContact"
							className="form-control"
							value={from.contact}
							onChange={handleFromChange("contact")}
							data-test-id="nonrkpayinvoicedetail__from-contact"
						/>
						<div className="visible-print-block">{from.contact}</div>
					</div>
				</div>
				{/* Invoice recipient details */}
				<div className="col-xs-12 col-sm-6 col-md-5 col-lg-4 col-md-offset-2 col-lg-offset-4 invoice-bill-to">
					<h4 className="bold-text text-right text-left-xs">Bill To</h4>
					<div className="form-group">
						<label htmlFor="selectedClient">{lang.t("Name")}</label>
						<ReactSelect
							value={selectedClient.value}
							name="selectedClient"
							id="selectedClient"
							className="hidden-print"
							placeholder="Search by business name"
							isLoading={clientList.loading}
							options={clientList.data}
							onChange={handleClientChange}
							isClearable
							isDisabled={paymentHistory.length > 0}
						/>
						<div className="visible-print-block">{selectedClient.label}</div>
					</div>
					<div className="form-group">
						<label htmlFor="streetAddress">{lang.t("Business Address")}</label>
						<input
							id="streetAddress"
							name="streetAddress"
							className="form-control"
							value={billTo.streetAddress}
							placeholder="Street Address"
							onChange={handleBillToChange("streetAddress")}
							data-test-id="nonrkpayinvoicedetail__billto-address"
						/>
						<div className="visible-print-block">{clientAddress}</div>
					</div>
					<div className="form-group hidden-print">
						<label htmlFor="city">{lang.t("City")}</label>
						<input
							id="city"
							name="city"
							className="form-control"
							value={billTo.city}
							placeholder="City"
							onChange={handleBillToChange("city")}
							data-test-id="nonrkpayinvoicedetail__billto-city"
						/>
					</div>
					<div className="form-group col-xs-6 statezip hidden-print">
						<label htmlFor="state">{lang.t("State")}</label>
						<StateSelection
							ref={ref => {
								_stateSelection = ref;
							}}
							className="form-control"
							name="stateId"
							id="stateId"
							placeholder="State"
							value={billTo.stateId}
							onChange={onChangeBillToState}
							dataTestId="nonrkpayinvoicedetail__billto-state"
						/>
					</div>
					<div className="form-group col-xs-6 statezip hidden-print">
						<label htmlFor="zipCode">{lang.t("Zip Code")}</label>
						<input
							type="tel"
							id="zipcode"
							name="zipcode"
							className="form-control"
							maxLength="5"
							placeholder="Zip Code"
							value={billTo.zip}
							onChange={handleBillToChange("zip")}
							data-test-id="nonrkpayinvoicedetail__billto-zip"
						/>
					</div>
					<div className="form-group">
						<label htmlFor="billingPhone">{lang.t("Phone Number")}</label>
						<MaskedInput
							type="tel"
							id="billingPhone"
							name="billingPhone"
							className="form-control"
							mask={MASK_VALUE.phoneMask}
							value={billTo.phone}
							placeholder="(xxx) xxx-xxxx"
							onChange={handleBillToChange("phone")}
							data-test-id="nonrkpayinvoicedetail__billto-phone"
						/>
						<div className="visible-print-block">{billTo.phone}</div>
					</div>
					<div className="form-group">
						<label htmlFor="billingEmail">{lang.t("Email")}</label>
						<input
							id="billingEmail"
							name="billingEmail"
							className="form-control"
							value={billTo.email}
							onChange={handleBillToChange("email")}
							data-test-id="nonrkpayinvoicedetail__billto-email"
						/>
						<div className="visible-print-block">{billTo.email}</div>
					</div>
					<div className="form-group">
						<label htmlFor="billingContact">{lang.t("Contact")}</label>
						<input
							id="billingContact"
							name="billingContact"
							className="form-control"
							value={billTo.contact}
							onChange={handleBillToChange("contact")}
							data-test-id="nonrkpayinvoicedetail__billto-contact"
						/>
						<div className="visible-print-block">{billTo.contact}</div>
					</div>
				</div>
			</div>
			<div className="row">
				<div className="col-xs-6 col-sm-3 col-md-2">
					<Switch
						label={lang.t("Group by Officer")}
						on={officerViewList.length > 0}
						onClick={toggleOfficerView}
					/>
				</div>
				<If condition={!user.isSuperAdmin()}>
					<div className="col-xs-6 col-sm-2 col-sm-offset-6 col-md-offset-8">
						<button
							type="button"
							className={classNames("btn btn-link text-right btn-block", {
								invisible: !showRefreshButton,
							})}
							id="update-items"
							onClick={confirmLineItemsRefresh}
						>
							{lang.t("Apply Job Updates")}
						</button>
					</div>
				</If>
			</div>
			<div className="separator" />
			<Choose>
				<When condition={officerViewList.length > 0}>
					{/* Officer grouped headers */}
					<div className="row job-headers hidden-xs hidden-sm">
						<div className="col-md-3">
							<strong>{lang.t("Officer")}</strong>
						</div>
						<div className="col-md-3">
							<strong>{lang.t("Job Name")}</strong>
						</div>
						<div className="col-md-3">
							<strong>
								{lang.t("Date")} &amp; {lang.t("Time")}
							</strong>
						</div>
						<div className="col-md-1">
							<strong>{lang.t("Rate")}</strong>
						</div>
						<div className="col-md-1">
							<strong>{lang.t("Hours")}</strong>
						</div>
						<div className="col-md-1 text-right print-amount">
							<strong>{lang.t("Total")}</strong>
						</div>
					</div>
					{/* Officer grouped items */}
					<div className="container-fluid table-body">
						{officerViewList && officerViewList.map(officer =>
							<OfficerNonRKPayInvoiceItems
								key={`officer_${officer.OfficerId}`}
								user={user}
								officer={officer}
								lineItems={lineItems}
								nonRKPayInvoiceDetailHook={nonRKPayInvoiceDetailHook}
								getDisputeReasonsByJobId={getDisputeReasonsByJobId}
							/>
						)}
					</div>
				</When>
				<Otherwise>
					{/* Invoice line item headers */}
					<div className="row job-headers hidden-xs hidden-sm">
						<div className="col-md-1">
							<strong>{lang.t("Job ID")}</strong>
						</div>
						<div className="col-md-2">
							<strong>{lang.t("Job Name")}</strong>
						</div>
						<div className="col-md-2">
							<strong>
								{lang.t("Date")} &amp; {lang.t("Time")}
							</strong>
						</div>
						<div className="col-md-2">
							<strong>{lang.t("Officer")}</strong>
						</div>
						<div className="col-md-1">
							<strong>{lang.t("Rate")}</strong>
						</div>
						<div className="col-md-1">
							<strong>{lang.t("Hours")}</strong>
						</div>
						<div className="col-md-2">
							<strong>{lang.t("Fees")}</strong>
						</div>
						<div className="col-md-1 text-right print-amount">
							<strong>{lang.t("Total")}</strong>
						</div>
					</div>
					{/* Invoice line items */}
					<div className="container-fluid table-body">
						{lineItems && lineItems.map((item, index) => {
							if (item.Finalized) {
								return (
									<NonRKPayInvoiceItemView
										key={`item-${item.JobAssignmentId ?? `${item.JobId}-${index}`}`}
										user={user}
										item={item}
										nonRKPayInvoiceDetailHook={nonRKPayInvoiceDetailHook}
									/>
								)
							}
							return (
								<NonRKPayInvoiceItemForm
									key={`item-${item.JobAssignmentId ?? `${item.JobId}-${index}`}`}
									user={user}
									item={item}
									nonRKPayInvoiceDetailHook={nonRKPayInvoiceDetailHook}
									getDisputeReasonsByJobId={getDisputeReasonsByJobId}
									officerMiscFeesV3={officerMiscFeesV3}
								/>
							)
						})}
						<If condition={loadingLineItems}>
							<div className="row text-center">
								<i className="glyphicon glyphicon-repeat rkf-spin rkf-fw" />
							</div>
						</If>
					</div>
				</Otherwise>
			</Choose>
			<If condition={!hideAddJobsButton}>
				<div className="row addjobs-row form-group">
					<div className="col-xs-6 col-sm-3 col-md-2">
						<button
							type="button"
							id="add-jobs"
							className="btn btn-primary btn-block"
							disabled={!selectedClient.value}
							title={
								selectedClient.value
									? buttonTitle
									: lang.t("Select a client to add jobs")
							}
							onClick={handleClickAddJobs}
							data-test-id="nonrkpayinvoicedetail__add-jobs"
						>
							{`+ ${lang.t("Add Jobs")}`}
						</button>
					</div>
				</div>
			</If>
			{/* Invoice summary */}
			<If condition={lineItems.length > 0}>
				<div className="row form-section payment-spacer">
					<div
						ref={ref => {
							if (ref) registerCardRef(feesAndTaxesDispute?.Id, ref)
						}}
						className={classNames("col-xs-12 col-sm-5 col-sm-offset-7 text-right", {
							"border-1 border-solid border-danger": Boolean(feesAndTaxesDispute),
						})}>
						<div className="row payment-spacer">
							<If condition={!excludeOfficerRate}>
								<div className="col-xs-9">
									<strong>{lang.t("Officer Hourly Total")}</strong>
								</div>
								<div className="col-xs-3">
									{utils.currencyFormatter(totals.hourlyTotal)}
								</div>
							</If>
							<If condition={excludeOfficerRate}>
								<div className="col-xs-9">
									<div className="exclude-fees">
										<strong style={{ color: "darkgrey" }}>
											{lang.t("Officer Pay Excluded")}
										</strong>
									</div>
								</div>
								<div className="col-xs-3">---</div>
							</If>
						</div>
						<Choose>
							<When condition={showFeesBreakdown}>
								<If condition={officerMiscFeesV3}>
									<div className={classNames("row payment-spacer", {
										"text-danger": feesAndTaxesDispute?.FeeType === DISPUTE_FEE_TYPE.OFFICER_MISC_FEE
									})}>
										<div className="col-xs-9">
											<strong>{`${lang.t("Officer Fees")} ${lang.t("Total")}`}</strong>
										</div>
										<div className="col-xs-3">
											{utils.currencyFormatter(totals.miscPersonnelFeesTotal)}
										</div>
									</div>
								</If>
								<div className={classNames("row payment-spacer", {
									"text-danger": feesAndTaxesDispute?.FeeType === DISPUTE_FEE_TYPE.AGENCY
								})}>
									<div className="col-xs-9">
										<strong>{`${lang.t("Agency")} ${lang.t("Fees Total")}`}</strong>
									</div>
									<div className="col-xs-3">
										{utils.currencyFormatter(totals.agencyFeesTotal)}
									</div>
								</div>
								<div className={classNames("row payment-spacer", {
									"text-danger": feesAndTaxesDispute?.FeeType === DISPUTE_FEE_TYPE.VEHICLE
								})}>
									<div className="col-xs-9">
										<strong>{`${lang.t("Vehicle")} ${lang.t("Fees Total")}`}</strong>
									</div>
									<div className="col-xs-3">
										{utils.currencyFormatter(totals.vehicleFeesTotal)}
									</div>
								</div>
								<div className={classNames("row payment-spacer", {
									"text-danger": feesAndTaxesDispute?.FeeType === DISPUTE_FEE_TYPE.ASSET
								})}>
									<div className="col-xs-9">
										<strong>{`${lang.t("Equipment")} ${lang.t("Fees Total")}`}</strong>
									</div>
									<div className="col-xs-3">
										{utils.currencyFormatter(totals.equipmentFeesTotal)}
									</div>
								</div>
								<If condition={isInvoicingV3}>
									<div className={classNames("row payment-spacer", {
										"text-danger": feesAndTaxesDispute?.FeeType === DISPUTE_FEE_TYPE.COORDINATOR
									})}>
										<div className="col-xs-9">
											<strong>{`${lang.t("Coordination")} ${lang.t("Fees Total")}`}</strong>
										</div>
										<div className="col-xs-3">
											{utils.currencyFormatter(totals.coordinationFeesTotal)}
										</div>
									</div>
								</If>
							</When>
							<Otherwise>
								<div className="row payment-spacer">
									<div className="col-xs-9">
										<strong>{lang.t("Fees Total")}</strong>
									</div>
									<div className="col-xs-3">
										{utils.currencyFormatter(totals.feesTotal)}
									</div>
								</div>
							</Otherwise>
						</Choose>
						<If condition={!hideInvoiceLevelFees}>
							<div className="row add-fee text-left payment-spacer hidden-print">
								<div className="separator separator-solid" />
								<div className="col-xs-8 form-group">
									<label
										className="bold-text"
										htmlFor="customFeeName"
									>
										<small>{lang.t("Fee")}</small>
									</label>
									<input
										id="customFeeName"
										name="customFeeName"
										className={`form-control${customFee.nameError ? " has-error" : ""}`}
										placeholder={lang.t("Type of fee")}
										value={customFee.Name}
										onChange={handleCustomFeeChange("Name")}
										onKeyPress={({ key }) => {
											if (key === "Enter") createCustomFee();
										}}
									/>
								</div>
								<div className="col-xs-4 form-group text-right">
									<label
										className="bold-text"
										htmlFor="customFeeTotal"
									>
										<small>{`${lang.t("Fee Amount")} ($)`}</small>
									</label>
									<input
										type="tel"
										id="customFeeTotal"
										name="customFeeTotal"
										className={`form-control text-right${customFee.totalError ? " has-error" : ""}`}
										value={customFee.Total}
										onChange={handleCustomFeeChange("Total")}
										onKeyPress={({ key }) => {
											if (key === "Enter") createCustomFee();
										}}
									/>
								</div>
								<div className="col-xs-12">
									<button
										type="button"
										id="add-new-fee"
										className="btn btn-link"
										onClick={() => createCustomFee()}
										disabled={loadingInvoiceTotals}
									>
										<small>{`+ ${lang.t("Add Fee")}`}</small>
									</button>
								</div>
							</div>
						</If>
						<SectionSeparator />
						<For
							each="fee"
							of={totals.additionalFees}
							index="index"
						>
							<div
								className="row"
								key={`additional_fee_${fee.Name}${fee.Total}`}
							>
								<div className="col-xs-9">
									<button
										type="button"
										className="btn-link text-center hidden-print"
										onClick={() => deleteCustomFee(index)}
									>
										<img
											className="trash-icon"
											src={TrashIcon}
											width="20"
											height="20"
											alt="Delete custom fee"
											aria-label="Delete custom fee"
										/>
									</button>
									<strong>{fee.Name}</strong>
								</div>
								<div className="col-xs-3">{utils.currencyFormatter(fee.Total)}</div>
							</div>
						</For>
						<If condition={isInvoicingV3}>
							<If condition={totals.subTotal !== null}>
								<div className="row payment-spacer">
									<div className="col-xs-9">
										<strong>{isEstimate ? lang.t("Estimated Subtotal") : lang.t("Subtotal")}</strong>
									</div>
									<div className="col-xs-3">
										{utils.currencyFormatter(totals.subTotal)}
									</div>
								</div>
							</If>
							<If condition={totals.serviceFee !== null}>
								<div className={classNames("row payment-spacer", {
									"text-danger": feesAndTaxesDispute?.FeeType === DISPUTE_FEE_TYPE.SERVICE
								})}>
									<div className="col-xs-9">
										<strong>{isEstimate ? lang.t("Estimated Service Fee") : lang.t("Service Fee")}</strong>
									</div>
									<div className="col-xs-3">
										{utils.currencyFormatter(totals.serviceFee)}
									</div>
								</div>
							</If>
							<If
								condition={
									totals.workerCompensationFee !== null &&
									totals.workerCompensationFee > 0
								}
							>
								<div className="row payment-spacer">
									<div className="col-xs-9">
										<strong>{isEstimate ? lang.t("Estimated Workers' Compensation Fee") : lang.t("Workers' Compensation Fee")}</strong>
									</div>
									<div className="col-xs-3">
										{utils.currencyFormatter(totals.workerCompensationFee)}
									</div>
								</div>
							</If>
							<If condition={totals.tax !== null}>
								<div className={classNames("row payment-spacer", {
									"text-danger": feesAndTaxesDispute?.FeeType === DISPUTE_FEE_TYPE.TAXES
								})}>
									<div className="col-xs-9">
										<strong>{isEstimate ? lang.t("Estimated Tax") : lang.t("Service Tax")}</strong>
									</div>
									<div className="col-xs-3">
										{utils.currencyFormatter(totals.tax)}
									</div>
								</div>
							</If>
							<SectionSeparator />
						</If>
						<div className="row">
							<div className="col-xs-9">
								<strong>{isEstimate ? lang.t("Estimated Total") : lang.t("Total")}</strong>
							</div>
							<div className="col-xs-3">
								{utils.currencyFormatter(totals.totalDue)}
							</div>
						</div>
						<PaymentsAppliedDisplay
							paymentHistory={paymentHistory}
							totals={totals}
							nonrkpayDetailPrepayEnhancementsEnabled={nonrkpayDetailPrepayEnhancementsEnabled}
							showToggle
							showPaymentHistory={showPaymentHistory}
							togglePaymentHistory={togglePaymentHistory}
							refundsInTransit={refundsInTransit}
						/>
						<SectionSeparator />
						<div className="row">
							<div className="col-xs-9">
								<strong>{lang.t("Balance Due")}</strong>
							</div>
							<div className="col-xs-3">
								{utils.currencyFormatter(totals.balance)}
							</div>
						</div>
						<SectionSeparator />
						{nonrkpayDetailPrepayEnhancementsEnabled && (
							<>
								{isEstimate && (
									<>
										<AvailableFundsDisplay availableFunds={availableFunds} />
										<SectionSeparator />
									</>
								)}
								{(totals.refunds < 0 || refundsInTransit?.length > 0) && (
									<>
										<RefundsDisplay
											totals={totals}
											paymentHistory={paymentHistory}
											refundsInTransit={refundsInTransit}
										/>
										<SectionSeparator />
									</>
								)}
							</>
						)}
						<InvoiceDisclaimer
							disclaimer={invoiceDisclaimer}
							handleInvoiceChange={handleInvoiceChange}
							showTextArea
						/>
					</div>
				</div>
				<If condition={excludeOfficerRate}>
					<div className="row">
						<div className="col-xs-12 col-sm-6 exlude-fees-alert">
							<div className="row">
								<div className="col-sm-12">
									<label
										htmlFor="attention"
										style={{ color: "red" }}
									>
										{lang.t("Attention")}
									</label>
								</div>
							</div>
							<div className="row">
								<div className="col-sm-12">
									<label className="attention-message">
										{lang.t("Attention Exclude Pay TextBox")}
									</label>
								</div>
							</div>
						</div>
					</div>
				</If>
				<If condition={(isEstimate && !sentDateTimeUTC) || (!isEstimate && !finalized)}>
					<div className="row hidden visible-print">
						<div className="col-xs-12">
							<div className="row">
								<div className="col-sm-12">
									<label
										htmlFor="attention"
										style={{ color: "red" }}
									>
										{lang.t("Attention")}
									</label>
								</div>
							</div>
							<div className="row">
								<div className="col-sm-12">
									<label className="attention-message">
										{`${lang.t(isEstimate ? "PDF Disclaimer message for estimates not sent to clients" : "PDF Disclaimer message for invoices not sent to clients")}`}
									</label>
								</div>
							</div>
						</div>
					</div>
				</If>
			</If>
		</div >
	);
};

NonRKPayInvoiceDetailForm.propTypes = {
	user: PropTypes.object,
	clientAddress: PropTypes.string,
	officerMiscFeesV3: PropTypes.bool,
	clientV4InvoicingDisputes: PropTypes.bool,
	nonRKPayInvoiceDetailHook: PropTypes.object.isRequired,
	query: PropTypes.object,
};

NonRKPayInvoiceDetailForm.defaultProps = {
	user: {},
	officerMiscFeesV3: false,
	clientV4InvoicingDisputes: false,
	clientAddress: "",
	query: {},
};

export default NonRKPayInvoiceDetailForm;
